// import "antd/dist/antd.css";

/**
 * ENHANCED FINANCE REQUESTS WITH DUPLICATE PREVENTION
 * ===================================================
 * 
 * This component implements sophisticated logic to prevent counting the same order
 * multiple times across different finance requests in the Finance Updates tab.
 * 
 * Key Features:
 * - Prevents counting the same order multiple times across different requests
 * - Only counts order amounts on the date of the first request
 * - Maintains consistency with business logic and timestamp thresholds
 * - Provides accurate financial summaries without duplicate order amounts
 * 
 * The calculateOrderAmountWithBusinessLogic function ensures that:
 * 1. Each order is identified uniquely across multiple finance requests
 * 2. Only the first request date for each order contributes to totals
 * 3. Subsequent requests for the same order are excluded from amount calculations
 * 4. All groupings (date, project, time ranges) use consistent logic
 * 
 * Example:
 * - Order #12345 (₹100,000) has requests on Jan 1st, Jan 5th, Jan 10th
 * - Amount will only be counted on Jan 1st (first request)
 * - Total for Jan 1st: ₹100,000, Jan 5th: ₹0, Jan 10th: ₹0
 * - Overall total remains ₹100,000 instead of ₹300,000
 */

import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import "./Req_Rec_Card.scss"; // some styles here
import Req_Rec_Card from "./Req_Rec_Card";
import OpportunityService from "../../api/opportunityService";
import { UserContext } from "../../Context/UserContext";
import { useLocation } from "react-router-dom";
// import { DatePicker, Space } from "antd";
import CustomDateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import { DropDownMUI } from "../../components";
import config from "../../config/config";
import {
  Document,
  PDFDownloadLink,
  Page,
  StyleSheet,
  View,
  Text,
  Link,
} from "@react-pdf/renderer";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { FaCalendarAlt, FaChevronRight, FaChevronDown } from "react-icons/fa";

// Use a fallback for environment variable
const currentEnvironment =
  typeof window !== "undefined" && window.environment
    ? window.environment
    : typeof process !== "undefined" &&
      process.env &&
      process.env.NODE_ENV === "production"
    ? "production"
    : "staging";

const isDateLiesCheck = (currentItemDate, DateRangeValue) => {
  console.log(
    "checking date for curent elemt",
    new Date(currentItemDate).getTime(),
    new Date(DateRangeValue[0]).getTime(),
    new Date(DateRangeValue[1]).getTime()
  );
  if (
    new Date(currentItemDate).getTime() >=
      new Date(DateRangeValue[0]).getTime() &&
    new Date(currentItemDate).getTime() <= new Date(DateRangeValue[1]).getTime()
  ) {
    console.log("current elemnt lies in btw");
    return true;
  }
  return false;
};

// Helper function to check if item is a first request
const isFirstRequestItem = (item) => {
  try {
    const parsedHistory = JSON.parse(item.History || "{}");
    const firstHistoryEntry = Object.values(parsedHistory)?.[0];
    return firstHistoryEntry?.firstRequest === true;
  } catch {
    return false;
  }
};

// Global order tracking registry - ensures orders are never counted more than once
const globalOrderRegistry = {
  countedOrders: new Set(),
  orderFirstRequestMap: new Map(),
  isInitialized: false,
  
  // Initialize the registry with all finance data
  initialize(allFinanceData) {
    this.countedOrders.clear();
    this.orderFirstRequestMap.clear();
    
    // First pass: identify all unique orders and their first request dates
    const orderMap = new Map();
    
    allFinanceData.forEach(item => {
      // Only process requests, not records (though Requests_Fin only has requests)
      const orderId = getOrderIdentifier(item);
      const firstRequestDate = getFirstRequestDate(item);
      
      if (!orderMap.has(orderId)) {
        orderMap.set(orderId, {
          firstRequestDate,
          amount: parseFloat(item.OrderTotalAmount || 0),
          firstRequestItem: item
        });
      } else {
        // Update with the earliest first request date
        const existing = orderMap.get(orderId);
        if (firstRequestDate && (!existing.firstRequestDate || firstRequestDate < existing.firstRequestDate)) {
          orderMap.set(orderId, {
            firstRequestDate,
            amount: parseFloat(item.OrderTotalAmount || 0),
            firstRequestItem: item
          });
        }
      }
    });
    
    // Second pass: determine which specific requests should contribute to totals
    allFinanceData.forEach(item => {
      const orderId = getOrderIdentifier(item);
      const orderData = orderMap.get(orderId);
      if (!orderData) return;
      
      let parsedHistory;
      try {
        parsedHistory = JSON.parse(item.History || "{}");
      } catch {
        parsedHistory = {};
      }
      
      const firstHistoryEntry = Object.values(parsedHistory)?.[0];
      const entryTime = firstHistoryEntry?.date;
      
      // Check if this specific item represents the first request for this order
      const isThisTheFirstRequest = orderData.firstRequestDate === entryTime;
      
      if (isThisTheFirstRequest) {
        // Determine if this order should be counted based on business logic
        const timestampThreshold = 1747699200000;
        let shouldCount = false;
        
        if (entryTime > timestampThreshold) {
          // Use filtered logic: only if firstRequest === true
          const isFirstRequest = firstHistoryEntry?.firstRequest === true;
          shouldCount = isFirstRequest;
        } else {
          // Use general logic for older entries
          shouldCount = true;
        }
        
        if (shouldCount) {
          const amount = parseFloat(item.OrderTotalAmount || 0);
          if (!isNaN(amount) && amount > 0) {
            // Map this specific request item to be the counting instance for this order
            this.orderFirstRequestMap.set(orderId, {
              item: item,
              amount: amount,
              shouldCount: true
            });
          }
        }
      }
    });
    
    this.isInitialized = true;
    console.log('🔧 Global Order Registry Initialized (Requests):', {
      totalUniqueOrders: orderMap.size,
      ordersToCount: this.orderFirstRequestMap.size,
      countedOrders: Array.from(this.orderFirstRequestMap.keys())
    });
  },
  
  // Check if a specific request should contribute to the total
  shouldCountOrder(item) {
    if (!this.isInitialized) {
      console.warn('⚠️ Global Order Registry not initialized');
      return false;
    }
    
    const orderId = getOrderIdentifier(item);
    const orderData = this.orderFirstRequestMap.get(orderId);
    
    // Only count if this is the designated counting instance for this order
    return orderData && orderData.item === item && orderData.shouldCount;
  },
  
  // Get the amount for an order (if it should be counted)
  getOrderAmount(item) {
    if (!this.shouldCountOrder(item)) return 0;
    
    const orderId = getOrderIdentifier(item);
    const orderData = this.orderFirstRequestMap.get(orderId);
    return orderData ? orderData.amount : 0;
  },
  
  // Reset the registry
  reset() {
    this.countedOrders.clear();
    this.orderFirstRequestMap.clear();
    this.isInitialized = false;
  }
};

// Helper function to get unique order identifier
const getOrderIdentifier = (item) => {
  // Try multiple fields to create a unique identifier for the order
  if (item.OrderId) return `order_${item.OrderId}`;
  if (item.uuid) return `uuid_${item.uuid}`;
  if (item.id) return `id_${item.id}`;
  
  // Fallback: create identifier from order details
  const orderDetails = [
    item.OrderTotalAmount,
    item.ProjectDetails,
    item.VendorDetails
  ].filter(Boolean).join('_');
  
  return `fallback_${orderDetails}`;
};

// Helper function to get the first request date for an order
const getFirstRequestDate = (item) => {
  try {
    const parsedHistory = JSON.parse(item.History || "{}");
    const historyEntries = Object.values(parsedHistory);
    
    if (historyEntries.length === 0) return null;
    
    // Sort by date to find the earliest entry
    const sortedEntries = historyEntries.sort((a, b) => (a.date || 0) - (b.date || 0));
    return sortedEntries[0]?.date || null;
  } catch {
    return null;
  }
};

// Enhanced calculate total amount using global order tracking
const calculateOrderAmountWithBusinessLogic = (requests) => {
  if (!globalOrderRegistry.isInitialized) {
    console.warn('⚠️ Attempting calculation before global registry initialization');
    return 0;
  }
  
  return requests.reduce((total, request) => {
    // Use global registry to determine if this order should be counted
    const amount = globalOrderRegistry.getOrderAmount(request);
    return total + amount;
  }, 0);
};

const SelectedRequestsPDF = ({ selectedRequests, sumOfSelectedRequests }) => {
  const styles = StyleSheet.create({
    page: {
      marginTop: 20,
      fontSize: 12,
      width: "95%",
      display: "flex",
      // margin: "auto",
      // alignItems: "center",
      flexDirection: "column",
    },
    title: {
      backgroundColor: "#26428b",
      padding: 2,
      fontWeight: "bold",
      width: "100%",
    },
    // header: {
    //   display: "flex",
    //   width: "100%",
    //   flexDirection: "row",
    //   justifyContent: "space-between",
    //   borderStyle: "solid",
    //   borderBottomWidth: 3,
    //   borderBottomColor: "#26428b",
    //   marginLeft: 2,
    //   marginRight: 2,
    //   marginBottom: 3,
    // },
    mainContainer: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
    },
    // requestCard: {
    //   // padding: "1px 5px",
    //   // paddingVertical: 1,
    //   // paddingHorizontal: 5,
    //   // position: "relative",
    //   // margin: "4px 16px",
    //   // marginVertical: 4,
    //   // marginHorizontal: 16,
    //   // borderRadius: 10,
    //   // backgroundColor: "#d9d9d9",
    //   // boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    // },
    table: {
      display: "table",
      width: "100%",
    },
    row: {
      flexDirection: "row",
    },
    cell: {
      fontSize: 10,
      flexGrow: 1,
      padding: 5,
      // fontWeight: "400",
      fontWeight: 400,
      // grid-template-columns:
      //   minmax(0px, 4fr) minmax(0px, 4fr) minmax(0px, 4.5fr) minmax(0px, 3fr) minmax(0px, 3fr)
      //   minmax(0px, 2.5fr);
    },
    cell1: {
      flex: 5.5,
    },
    cell2: {
      flex: 5.5,
    },
    cell3: {
      flex: 4.5,
    },
    cell4: {
      flex: 3,
    },
    cell5: {
      flex: 3,
    },
    cell6: {
      flex: 2.5,
    },
    header: {
      backgroundColor: "#d9d9d9",
      borderStyle: "solid",
      borderBottomWidth: 1,
      borderBottomColor: "#26428b",
      marginTop: 4,
    },
    TotalAmountContainer: {
      marginTop: 15,

      alignSelf: "flex-end",
      width: "16%",
      textAlign: "center",
    },
    totalAmountheader: {
      borderStyle: "solid",
      borderBottomWidth: 1,
      borderBottomColor: "#26428b",
    },
  });
  return (
    <Document>
      <Page
        size="A4"
        orientation="landscape"
        style={{ width: "90vh", display: "flex", alignItems: "center" }}
      >
        <View style={styles.page}>
          <View style={styles.title}>
            <Text style={{ textAlign: "center", color: "white" }}>
              Payments ({new Date().toLocaleDateString("en-GB")})
            </Text>
          </View>
          <View style={styles.mainContainer}>
            <View style={styles.table}>
              <View style={[styles.row, styles.header]}>
                <View style={[styles.cell, styles.cell1]}>
                  <Text>Site</Text>
                </View>
                <View style={[styles.cell, styles.cell2]}>
                  <Text>Category</Text>
                </View>
                <View style={[styles.cell, styles.cell3]}>
                  <Text>Vendor</Text>
                </View>
                <View style={[styles.cell, styles.cell4]}>
                  <Text>Vendor Mobile</Text>
                </View>
                <View style={[styles.cell, styles.cell5]}>
                  <Text>PO Amount</Text>
                </View>
                <View style={[styles.cell, styles.cell6]}>
                  <Text>Requested</Text>
                </View>
              </View>
              {Object.values(selectedRequests).map((request, index) => {
                // let History = JSON.parse(request?.History);
                // let latestEDIT =
                //   History &&
                //   Object.values(History)[Object.values(History).length - 1];
                return (
                  <View
                    key={request.uuid}
                    style={[
                      {
                        backgroundColor: `${
                          index % 2 === 0 ? "#f1eeee" : "white"
                        }`,
                      },
                    ]}
                  >
                    <View style={styles.row}>
                      <View style={[styles.cell, styles.cell1]}>
                        <Text>
                          {Object.values(JSON.parse(request.ProjectDetails))[0]}
                        </Text>
                      </View>
                      <View style={[styles.cell, styles.cell2]}>
                        <Text>{request.Category || "\u2013\u2013"}</Text>
                      </View>
                      <View style={[styles.cell, styles.cell3]}>
                        <Text>{request.VendorName || "\u2013\u2013"}</Text>
                      </View>
                      <View style={[styles.cell, styles.cell4]}>
                        <Text>{request.VendorMobile || "\u2013\u2013"}</Text>
                      </View>
                      <View style={[styles.cell, styles.cell5]}>
                        <Text>
                          {request.OrderTotalAmount || "\u2013\u2013"}
                        </Text>
                        {/* <Text>
                          <Link
                            src={`https://startstaging.web.app/PO/${request.OrderId}`}
                          >
                            link
                          </Link>
                        </Text> */}
                      </View>
                      <View
                        style={[
                          styles.cell,
                          styles.cell6,
                          { fontWeight: "700" },
                        ]}
                      >
                        <Text>
                          {request.AmountRequested || "--"}
                          {"/- "}
                        </Text>
                      </View>
                    </View>
                    {/* <View style={styles.row}>
                      <View
                        style={[styles.headerCell, styles.cell, styles.cell1]}
                      >
                        <Text>Requested By</Text>
                      </View>
                      <View
                        style={[styles.headerCell, styles.cell, styles.cell2]}
                      >
                        <Text>Requested On</Text>
                      </View>
                      <View
                        style={[styles.headerCell, styles.cell, styles.cell3]}
                      >
                        <Text>Vendor Mobile</Text>
                      </View>
                      
                      <View style={[styles.cell, styles.cell4]}>
                     
                      </View>
                      <View style={[styles.cell, styles.cell5]}>
                        <Text></Text>
                      </View>
                    </View>
                    <View style={styles.row}>
                      <View
                        style={[styles.headerCell, styles.cell, styles.cell1]}
                      >
                        <Text>{Object.values(History)[0].By}</Text>
                      </View>

                      <View
                        style={[styles.headerCell, styles.cell, styles.cell2]}
                      >
                        <Text>
                          {new Date(request.DOFReq).toLocaleDateString("en-IN")}
                        </Text>
                      </View>
                      <View style={[styles.cell, styles.cell3]}>
                        <Text>{request.VendorMobile || "\u2013\u2013"}</Text>
                      </View>
                    

                      <View style={[styles.cell, styles.cell4]}>
                      
                      </View>
                      <View style={[styles.cell, styles.cell5]}>
                        <Text></Text>
                      </View>
                    </View> */}
                  </View>
                );
              })}
            </View>
            <View style={styles.TotalAmountContainer}>
              <Text style={styles.totalAmountheader}>Total Amount</Text>
              <Text>{sumOfSelectedRequests}</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// Helper component for date-wise grouping of requests
const DateGroup = ({
  date,
  requests,
  isOpen,
  toggleOpen,
  setFinRequestsData,
  setRawData,
  selectedRequests,
  handleCheckboxChange,
}) => {
  // Calculate total requested amount for this date group
  const totalRequestedAmount = useMemo(() => {
    return requests.reduce((total, request) => {
      const amount = parseFloat(request.AmountRequested || 0);
      return total + (isNaN(amount) ? 0 : amount);
    }, 0);
  }, [requests]);

  // Extract unique project names for this date group
  const projectNames = useMemo(() => {
    const uniqueProjects = new Set();
    requests.forEach((request) => {
      try {
        const projectDetails = JSON.parse(request.ProjectDetails || "{}");
        const projectName = Object.values(projectDetails)[0];
        if (projectName) {
          uniqueProjects.add(projectName);
        }
      } catch (error) {
        console.error("Error parsing project details:", error);
      }
    });
    return Array.from(uniqueProjects);
  }, [requests]);

  // const totalOrderAmount2 = useMemo(() => {
  //   return requests.reduce((total, request) => {
  //     const amount = parseFloat(request.OrderTotalAmount || 0);
  //     return total + (isNaN(amount) ? 0 : amount);
  //   }, 0);
  // }, [requests]);

  // const totalOrderAmount = useMemo(() => {

  //   // get from firebase for each date
  //   // know the date filtered the timestamp
  //   // develope logic with chat gpt
  //   const timestampThreshold = 1747699200000;

  //   return requests.reduce((total, request) => {
  //     let parsedHistory;

  //     try {
  //       parsedHistory = JSON.parse(request.History || "{}");
  //     } catch {
  //       parsedHistory = {};
  //     }

  //     const firstHistoryEntry = Object.values(parsedHistory)?.[0];
  //     const entryTime = firstHistoryEntry?.date;
  //     if (entryTime > timestampThreshold) {
  //       // Use filtered logic: only if firstRequest === true
  //       const isFirstRequest = firstHistoryEntry?.firstRequest === true;
  //       if (!isFirstRequest) return total;

  //       const amount = parseFloat(request.OrderTotalAmount || 0);
  //       return total + (isNaN(amount) ? 0 : amount);
  //     } else {
  //       // Use general logic (totalOrderAmount2)
  //       const amount = parseFloat(request.OrderTotalAmount || 0);
  //       return total + (isNaN(amount) ? 0 : amount);
  //     }
  //   }, 0);
  // }, [requests]);

  const totalOrderAmount = useMemo(() => {
    return calculateOrderAmountWithBusinessLogic(requests);
  }, [requests]);

  const requestSummary = useMemo(() => {
    return requests.reduce((summary, request) => {
      const status = request.Status;

      // Parse the History to get the most recent status
      try {
        const history = JSON.parse(request.History || "{}");
        const latestEntry =
          Object.values(history)[Object.values(history).length - 1] || {};
        const latestStatus = latestEntry.status || status;

        // Set default if this status hasn't been seen yet
        if (!summary[latestStatus]) {
          summary[latestStatus] = { count: 0, amount: 0 };
        }

        // Increment the count and add the amount
        summary[latestStatus].count += 1;
        summary[latestStatus].amount += Number(request.AmountRequested) || 0;
      } catch (error) {
        console.error("Error parsing history:", error);
      }

      return summary;
    }, {});
  }, [requests]);

  // Generate project colors based on name hash
  const getProjectColor = (name) => {
    // List of pleasing pastel colors
    const colors = [
      { bg: "#E6F2FF", border: "#A6D0FF", text: "#2E78D2" }, // blue
      { bg: "#FFE6E6", border: "#FFBFBF", text: "#D23A3A" }, // red
      { bg: "#E6FFEF", border: "#BFFFDB", text: "#279E5A" }, // green
      { bg: "#FFF4E6", border: "#FFE0B3", text: "#D2880A" }, // orange
      { bg: "#F4E6FF", border: "#E0BFFF", text: "#9433D2" }, // purple
      { bg: "#FFFFE6", border: "#FFFFBF", text: "#858500" }, // yellow
      { bg: "#E6FFF9", border: "#BFFFEE", text: "#00857B" }, // teal
      { bg: "#F5E6FF", border: "#E5BFFF", text: "#8333D2" }, // lavender
    ];

    // Use string hash to pick a color
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  // Get formatted date in a shorter version for display
  const getShortDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div
      className="date-group-container"
      style={{
        marginBottom: "14px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid #f0f0f0",
        backgroundColor: isOpen ? "#fffdf5" : "#ffffff",
        width: "100%",
        transition: "all 0.2s ease",
      }}
    >
      <div
        className="date-group-header"
        onClick={toggleOpen}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "14px 20px",
          backgroundColor: isOpen ? "#fffdf5" : "#ffffff",
          cursor: "pointer",
          borderLeft: isOpen ? `4px solid #fdd34d` : "4px solid transparent",
          borderBottom: isOpen ? "1px solid #f0f0f0" : "none",
          transition: "all 0.3s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            flexDirection: "column",
            width: "25%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <span
              style={{
                marginRight: "12px",
                fontSize: "15px",
                color: "#fdd34d",
                transform: isOpen ? "rotate(90deg)" : "rotate(0)",
                display: "inline-block",
                width: "18px",
                height: "18px",
                textAlign: "center",
                lineHeight: "18px",
                fontWeight: "bold",
                transition: "transform 0.3s ease",
              }}
            >
              ▶
            </span>
            <h3
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: "600",
                color: "#333",
              }}
            >
              {getShortDate(date)}
              <span
                style={{ color: "#006400" }}
              >{` | ₹ ${totalOrderAmount.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}`}</span>
            </h3>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginTop: "5px",
              marginLeft: "30px",
              gap: "15px",
            }}
          >
            <span
              style={{
                fontSize: "12px",
                color: "#555",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#606060",
                }}
              ></span>
              Total: {requests.length}
            </span>

            {requestSummary.Approved && (
              <span
                style={{
                  fontSize: "12px",
                  color: "#117539",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#117539",
                  }}
                ></span>
                Approved: {requestSummary.Approved.count}
              </span>
            )}

            {requestSummary.Requested && (
              <span
                style={{
                  fontSize: "12px",
                  color: "#1485AE",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#1485AE",
                  }}
                ></span>
                Pending: {requestSummary.Requested.count}
              </span>
            )}
          </div>

          {projectNames.length > 0 && (
            <div
              style={{
                marginLeft: "30px",
                marginTop: "6px",
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                maxWidth: "100%",
              }}
            >
              {projectNames.slice(0, 3).map((project, index) => {
                const colorScheme = getProjectColor(project);
                return (
                  <span
                    key={index}
                    style={{
                      fontSize: "11px",
                      backgroundColor: colorScheme.bg,
                      border: `1px solid ${colorScheme.border}`,
                      borderRadius: "12px",
                      padding: "2px 8px",
                      color: colorScheme.text,
                      whiteSpace: "nowrap",
                      maxWidth: "150px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {project}
                  </span>
                );
              })}
              {projectNames.length > 3 && (
                <span
                  style={{
                    fontSize: "11px",
                    backgroundColor: "#f2f2f2",
                    border: "1px solid #ddd",
                    borderRadius: "12px",
                    padding: "2px 8px",
                    color: "#555",
                  }}
                >
                  +{projectNames.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            width: "60%",
            justifyContent: "space-evenly",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "10px 16px",
              backgroundColor: "rgba(40, 199, 111, 0.1)",
              borderRadius: "8px",
              minWidth: "140px",
              flex: 1,
            }}
          >
            <span
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#117539",
              }}
            >
              Approved ({requestSummary.Approved?.count || 0})
            </span>
            <span
              style={{
                fontWeight: "700",
                fontSize: "16px",
                color: "#117539",
                marginTop: "4px",
              }}
            >
              ₹
              {(requestSummary.Approved?.amount || 0).toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "10px 16px",
              backgroundColor: "rgba(20, 133, 174, 0.1)",
              borderRadius: "8px",
              minWidth: "140px",
              flex: 1,
            }}
          >
            <span
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#1485AE",
              }}
            >
              Pending ({requestSummary.Requested?.count || 0})
            </span>
            <span
              style={{
                fontWeight: "700",
                fontSize: "16px",
                color: "#1485AE",
                marginTop: "4px",
              }}
            >
              ₹
              {(requestSummary.Requested?.amount || 0).toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
            </span>
          </div>

          {requestSummary.Declined && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "10px 16px",
                backgroundColor: "rgba(201, 0, 0, 0.1)",
                borderRadius: "8px",
                minWidth: "140px",
                flex: 1,
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#C90000",
                }}
              >
                Declined ({requestSummary.Declined.count})
              </span>
              <span
                style={{
                  fontWeight: "700",
                  fontSize: "16px",
                  color: "#C90000",
                  marginTop: "4px",
                }}
              >
                ₹
                {requestSummary.Declined.amount.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
              </span>
            </div>
          )}

          {requestSummary["On hold"] && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "10px 16px",
                backgroundColor: "rgba(242, 201, 76, 0.1)",
                borderRadius: "8px",
                minWidth: "140px",
                flex: 1,
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#b09000",
                }}
              >
                On Hold ({requestSummary["On hold"].count})
              </span>
              <span
                style={{
                  fontWeight: "700",
                  fontSize: "16px",
                  color: "#b09000",
                  marginTop: "4px",
                }}
              >
                ₹
                {requestSummary["On hold"].amount.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
              </span>
            </div>
          )}
        </div>

        <div
          style={{
            fontWeight: "bold",
            fontSize: "15px",
            color: "#333",
            backgroundColor: "#fdd34d",
            padding: "10px 16px",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(253, 211, 77, 0.25)",
            minWidth: "130px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            width: "13%",
          }}
        >
          <span style={{ fontSize: "13px" }}>Total Amount</span>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: "16px",
              fontWeight: "700",
            }}
          >
            <span style={{ fontSize: "14px", marginRight: "2px" }}>₹</span>
            <span>
              {totalRequestedAmount.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
            </span>
          </span>
        </div>
      </div>

      {isOpen && (
        <div
          className="date-group-content"
          style={{
            padding: "0",
            animation: "fadeIn 0.3s ease",
          }}
        >
          {requests.map((request, rIndex) => {
            const isFirstRequest = isFirstRequestItem(request);
            const shouldCountThisOrder = globalOrderRegistry.shouldCountOrder(request);
            return (
              <div
                key={request.uuid}
                style={{
                  position: "relative",
                  ...(isFirstRequest && {
                    border: "2px solid #fbbf24",
                    borderRadius: "12px",
                    backgroundColor: "#fffbeb",
                    padding: "8px",
                    margin: "4px 0",
                    boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)",
                  }),
                  ...(!shouldCountThisOrder && {
                    border: "2px solid #ef4444",
                    borderRadius: "12px",
                    backgroundColor: "#fef2f2",
                    opacity: 0.7,
                    padding: "8px",
                    margin: "4px 0",
                  }),
                }}
              >
                {isFirstRequest && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "8px",
                      backgroundColor: "#fbbf24",
                      color: "#78350f",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "10px",
                      fontWeight: "600",
                      zIndex: 1,
                    }}
                  >
                    FIRST REQUEST
                  </div>
                )}
                {!shouldCountThisOrder && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-8px",
                      left: "8px",
                      backgroundColor: "#ef4444",
                      color: "white",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "10px",
                      fontWeight: "600",
                      zIndex: 1,
                    }}
                  >
                    {/* NOT COUNTED (DUPLICATE ORDER) */}
                  </div>
                )}
                <Req_Rec_Card
                  request={request}
                  rIndex={rIndex}
                  setFinRequestsData={setFinRequestsData}
                  setRawData={setRawData}
                  selectedRequests={selectedRequests}
                  handleCheckboxChange={handleCheckboxChange}
                />
              </div>
            );
          })}
        </div>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};

const TimeBubbles = ({ viewMode, setViewMode }) => {
  // Define color palette for a professional look
  const colors = {
    primary: "#fdd34d",
    hover: "#003d7a",
    inactive: "#f8f9fa",
    border: "#dee2e6",
    text: "#212529",
    textLight: "#6c757d",
    textActive: "#353535",
  };

  // Container styles
  const containerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.5rem",
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    // boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    width: "fit-content",
    // margin: '1rem auto',
    // border: `1px solid ${colors.border}`
  };

  // Bubble base styles
  const bubbleBaseStyle = {
    padding: "0.625rem 1.25rem",
    margin: "0 0.25rem",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily:
      "SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, sans-serif",
    fontWeight: "500",
    fontSize: "0.875rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "5rem",
  };

  // Active bubble styles
  const activeBubbleStyle = {
    ...bubbleBaseStyle,
    backgroundColor: colors.primary,
    color: colors.textActive,
    boxShadow: "0 2px 8px rgba(0, 86, 179, 0.25)",
  };

  // Inactive bubble styles
  const inactiveBubbleStyle = {
    ...bubbleBaseStyle,
    backgroundColor: colors.inactive,
    color: colors.textLight,
    border: `1px solid ${colors.border}`,
  };

  // Demo component with state
  const [internalViewMode, setInternalViewMode] = useState(viewMode || "daily");

  const handleSetViewMode = (mode) => {
    if (setViewMode) {
      setViewMode(mode);
    } else {
      setInternalViewMode(mode);
    }
  };

  const currentViewMode = viewMode || internalViewMode;

  return (
    <div style={containerStyle}>
      <div
        style={
          currentViewMode === "daily" ? activeBubbleStyle : inactiveBubbleStyle
        }
        onClick={() => handleSetViewMode("daily")}
        onMouseOver={(e) => {
          if (currentViewMode !== "daily") {
            e.currentTarget.style.backgroundColor = "#f1f3f5";
          }
        }}
        onMouseOut={(e) => {
          if (currentViewMode !== "daily") {
            e.currentTarget.style.backgroundColor = colors.inactive;
          }
        }}
      >
        Daily
      </div>
      <div
        style={
          currentViewMode === "weekly" ? activeBubbleStyle : inactiveBubbleStyle
        }
        onClick={() => handleSetViewMode("weekly")}
        onMouseOver={(e) => {
          if (currentViewMode !== "weekly") {
            e.currentTarget.style.backgroundColor = "#f1f3f5";
          }
        }}
        onMouseOut={(e) => {
          if (currentViewMode !== "weekly") {
            e.currentTarget.style.backgroundColor = colors.inactive;
          }
        }}
      >
        Weekly
      </div>
      <div
        style={
          currentViewMode === "monthly"
            ? activeBubbleStyle
            : inactiveBubbleStyle
        }
        onClick={() => handleSetViewMode("monthly")}
        onMouseOver={(e) => {
          if (currentViewMode !== "monthly") {
            e.currentTarget.style.backgroundColor = "#f1f3f5";
          }
        }}
        onMouseOut={(e) => {
          if (currentViewMode !== "monthly") {
            e.currentTarget.style.backgroundColor = colors.inactive;
          }
        }}
      >
        Monthly
      </div>
    </div>
  );
};

// Component for a time group (week or month)
const TimeGroupHeader = ({
  dateRange,
  isOpen,
  toggleOpen,
  summary = {
    total: 0,
    approved: 0,
    pending: 0,
    declined: 0,
  },
  projects = [], // Array of project names in this time period
  projectGroups,
}) => {
  const formatDateRange = (range) => {
    if (!range || range.length !== 2) return "";

    const start = new Date(range[0]);
    const end = new Date(range[1]);

    const formatOptions = {
      day: "numeric",
      month: "short",
    };

    if (start.getFullYear() !== end.getFullYear()) {
      formatOptions.year = "numeric";
    }

    const startStr = start.toLocaleDateString("en-US", formatOptions);
    const endStr = end.toLocaleDateString("en-US", formatOptions);

    if (start.getTime() === end.getTime()) {
      return startStr;
    }

    return `${startStr} - ${endStr}`;
  };

  // Helper to get a stable color for a project
  const getProjectColor = (name) => {
    if (!name) return "#26428b";

    const colors = [
      "#26428b",
      "#2d7a80",
      "#4a6fa5",
      "#8e6e95",
      "#39a275",
      "#7a6cb0",
      "#d17e88",
      "#689f38",
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  // const totalOrderAmount = useMemo(() => {
  //   return Object.values(projectGroups).reduce((projectTotal, project) => {
  //     const requestSum = project.requests.reduce((sum, request) => {
  //       const amount = parseFloat(request.OrderTotalAmount || 0);
  //       return sum + (isNaN(amount) ? 0 : amount);
  //     }, 0);
  //     return projectTotal + requestSum;
  //   }, 0);
  // }, [projectGroups]);

  const totalOrderAmount = useMemo(() => {
    const timestampThreshold = 1747612800000;

    return Object.values(projectGroups).reduce((projectTotal, project) => {
      const requestSum = project.requests.reduce((sum, request) => {
        let parsedHistory;
        try {
          parsedHistory = JSON.parse(request.History || "{}");
        } catch {
          parsedHistory = {};
        }

        const firstHistoryEntry = Object.values(parsedHistory)?.[0];
        const entryTime = firstHistoryEntry?.date;
        const isFirstRequest = firstHistoryEntry?.firstRequest === true;

        const shouldInclude =
          entryTime <= timestampThreshold ||
          (entryTime > timestampThreshold && isFirstRequest);

        if (!shouldInclude) return sum;

        const amount = parseFloat(request.OrderTotalAmount || 0);
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);

      return projectTotal + requestSum;
    }, 0);
  }, [projectGroups]);

  return (
    <div
      className="time-group-header"
      onClick={toggleOpen}
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        padding: "16px",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
        border: `1px solid ${isOpen ? "#fdd34d" : "#e6e6e6"}`,
        borderLeft: "4px solid #fdd34d",
        marginBottom: "12px",
        cursor: "pointer",
        transition: "all 0.2s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "25%",
          }}
        >
          <FaCalendarAlt
            style={{
              color: "#777",
              marginRight: "10px",
              fontSize: "16px",
            }}
          />
          <span
            style={{
              fontWeight: "600",
              fontSize: "15px",
              color: "#333",
            }}
          >
            {formatDateRange(dateRange)}
            <span
              style={{ color: "#006400" }}
            >{` | ₹ ${totalOrderAmount.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}`}</span>
          </span>
          <span
            style={{
              marginLeft: "12px",
              color: "#777",
              display: "flex",
              alignItems: "center",
            }}
          >
            {isOpen ? (
              <FaChevronDown size={12} />
            ) : (
              <FaChevronRight size={12} />
            )}
          </span>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            alignItems: "center",
            width: "60%",
            gap: "10px",
          }}
        >
          {/* Always show the Approved card */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "8px 16px",
              backgroundColor: "rgba(40, 199, 111, 0.1)",
              borderRadius: "6px",
              minWidth: "140px",
              flex: 1,
            }}
          >
            <span
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#117539",
              }}
            >
              Approved ({summary.approvedCount || 0})
            </span>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#117539",
                marginTop: "2px",
              }}
            >
              ₹
              {summary.approved.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
            </span>
          </div>

          {/* Always show the Pending card */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "8px 16px",
              backgroundColor: "rgba(20, 133, 174, 0.1)",
              borderRadius: "6px",
              minWidth: "140px",
              flex: 1,
            }}
          >
            <span
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#1485AE",
              }}
            >
              Pending ({summary.pendingCount || 0})
            </span>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#1485AE",
                marginTop: "2px",
              }}
            >
              ₹
              {summary.pending.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
            </span>
          </div>

          {summary.declinedCount > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "8px 16px",
                backgroundColor: "rgba(201, 0, 0, 0.1)",
                borderRadius: "6px",
                minWidth: "140px",
                flex: 1,
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#C90000",
                }}
              >
                Declined ({summary.declinedCount || 0})
              </span>
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#C90000",
                  marginTop: "2px",
                }}
              >
                ₹
                {summary.declined.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
              </span>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px 16px",
            backgroundColor: "#fdd34d",
            borderRadius: "6px",
            width: "13%",
            minWidth: "130px",
          }}
        >
          <span
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: "#333",
            }}
          >
            Total ({summary.requestCount || 0})
          </span>
          <span
            style={{
              fontSize: "16px",
              fontWeight: "700",
              color: "#333",
              marginTop: "2px",
            }}
          >
            ₹
            {summary.total.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </span>
        </div>
      </div>

      {projects.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginTop: "12px",
          }}
        >
          {projects.slice(0, 8).map((project, index) => (
            <div
              key={index}
              style={{
                backgroundColor: "rgba(0,0,0,0.04)",
                color: getProjectColor(project),
                padding: "4px 10px",
                borderRadius: "4px",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: getProjectColor(project),
                }}
              ></div>
              {project}
            </div>
          ))}
          {projects.length > 8 && (
            <div
              style={{
                backgroundColor: "rgba(0,0,0,0.04)",
                color: "#555",
                padding: "4px 10px",
                borderRadius: "4px",
                fontSize: "12px",
              }}
            >
              +{projects.length - 8} more
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Project group component
const ProjectGroup = ({
  projectName,
  projectRequests,
  isOpen,
  toggleOpen,
  setFinRequestsData,
  setRawData,
  selectedRequests,
  handleCheckboxChange,
  summary = {
    total: 0,
    approved: 0,
    pending: 0,
    approvedCount: 0,
    pendingCount: 0,
  },
}) => {
  // Get project initial for the icon
  const getProjectInitial = (name) => {
    if (!name) return "?";
    return name.trim().charAt(0).toUpperCase();
  };

  // Get a stable color based on project name
  const getProjectColor = (name) => {
    if (!name) return "#26428b";

    const colors = [
      "#26428b",
      "#2d7a80",
      "#4a6fa5",
      "#8e6e95",
      "#39a275",
      "#7a6cb0",
      "#d17e88",
      "#689f38",
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  // const totalOrderAmount = useMemo(() => {
  //   return projectRequests.reduce((total, request) => {
  //     const amount = parseFloat(request.OrderTotalAmount || 0);
  //     return total + (isNaN(amount) ? 0 : amount);
  //   }, 0);
  // }, [projectRequests]);

  const totalOrderAmount = useMemo(() => {
    const timestampThreshold = 1747612800000;
  
    return projectRequests.reduce((total, request) => {
      let parsedHistory;
      try {
        parsedHistory = JSON.parse(request.History || '{}');
      } catch {
        parsedHistory = {};
      }
  
      const firstHistoryEntry = Object.values(parsedHistory)?.[0];
      const entryTime = firstHistoryEntry?.date;
      const isFirstRequest = firstHistoryEntry?.firstRequest === true;
  
      const shouldInclude =
        entryTime <= timestampThreshold || (entryTime > timestampThreshold && isFirstRequest);
  
      if (!shouldInclude) return total;
  
      const amount = parseFloat(request.OrderTotalAmount || 0);
      return total + (isNaN(amount) ? 0 : amount);
    }, 0);
  }, [projectRequests]); // GSTIN36AAJFS7559M1ZB SAI MARKETING
  

  return (
    <div
      className="project-group"
      style={{
        marginBottom: "10px",
        backgroundColor: "#ffffff",
        borderRadius: "6px",
        overflow: "hidden",
        border: "1px solid #e6e6e6",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        width: "100%",
      }}
    >
      <div
        className="project-group-header"
        onClick={toggleOpen}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          cursor: "pointer",
          borderBottom: isOpen ? "1px solid #eee" : "none",
          borderLeft: `4px solid ${getProjectColor(projectName)}`,
          backgroundColor: isOpen ? "#f8f9fa" : "#ffffff",
          transition: "all 0.2s ease",
        }}
      >
        <div
          className="project-title"
          style={{
            display: "flex",
            alignItems: "center",
            width: "25%",
          }}
        >
          <div
            className="project-icon"
            style={{
              backgroundColor: getProjectColor(projectName),
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "10px",
              fontSize: "13px",
              fontWeight: "700",
              color: "white",
            }}
          >
            {getProjectInitial(projectName)}
          </div>
          <span
            style={{
              fontWeight: "600",
              fontSize: "14px",
              color: "#333",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {projectName}
            <span
              style={{ color: "#006400" }}
            >{` | ₹ ${totalOrderAmount.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}`}</span>
          </span>
          <div
            className="toggle-icon"
            style={{
              display: "flex",
              alignItems: "center",
              marginLeft: "12px",
              color: "#777",
            }}
          >
            {isOpen ? (
              <FaChevronDown size={12} />
            ) : (
              <FaChevronRight size={12} />
            )}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "60%",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "space-evenly",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "8px 16px",
                backgroundColor: "rgba(40, 199, 111, 0.08)",
                borderRadius: "6px",
                flex: 1,
                minWidth: "130px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#117539",
                }}
              >
                Approved ({summary.approvedCount || 0})
              </span>
              <span
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#117539",
                  marginTop: "2px",
                }}
              >
                ₹
                {summary.approved.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "8px 16px",
                backgroundColor: "rgba(20, 133, 174, 0.08)",
                borderRadius: "6px",
                flex: 1,
                minWidth: "130px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#1485AE",
                }}
              >
                Pending ({summary.pendingCount || 0})
              </span>
              <span
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "#1485AE",
                  marginTop: "2px",
                }}
              >
                ₹
                {summary.pending.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px 16px",
            backgroundColor: "#fee8a0", // Lighter shade of #fdd34d
            borderRadius: "6px",
            minWidth: "120px",
            width: "12%",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: "#333",
            }}
          >
            Total
          </span>
          <span
            style={{
              fontSize: "15px",
              fontWeight: "700",
              color: "#333",
              marginTop: "2px",
            }}
          >
            ₹
            {summary.total.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </span>
        </div>
      </div>

      <div
        className={`project-requests ${isOpen ? "expanded" : ""}`}
        style={{
          maxHeight: isOpen ? "3000px" : "0px",
          overflow: "hidden",
          transition: "max-height 0.5s ease",
        }}
      >
        {projectRequests.map((request, rIndex) => {
          const isFirstRequest = isFirstRequestItem(request);
          const shouldCountThisOrder = globalOrderRegistry.shouldCountOrder(request);
          return (
            <div
              key={request.uuid}
              style={{
                position: "relative",
                ...(isFirstRequest && {
                  border: "2px solid #fbbf24",
                  borderRadius: "12px",
                  backgroundColor: "#fffbeb",
                  padding: "8px",
                  margin: "4px 0",
                  boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)",
                }),
                ...(!shouldCountThisOrder && {
                  border: "2px solid #ef4444",
                  borderRadius: "12px",
                  backgroundColor: "#fef2f2",
                  opacity: 0.7,
                  padding: "8px",
                  margin: "4px 0",
                }),
              }}
            >
              {isFirstRequest && (
                <div
                  style={{
                    position: "absolute",
                    top: "-8px",
                    right: "8px",
                    backgroundColor: "#fbbf24",
                    color: "#78350f",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "10px",
                    fontWeight: "600",
                    zIndex: 1,
                  }}
                >
                  FIRST REQUEST
                </div>
              )}
              {!shouldCountThisOrder && (
                <div
                  style={{
                    position: "absolute",
                    top: "-8px",
                    left: "8px",
                    backgroundColor: "#ef4444",
                    color: "white",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "10px",
                    fontWeight: "600",
                    zIndex: 1,
                  }}
                >
                  {/* NOT COUNTED (DUPLICATE ORDER) */}
                </div>
              )}
              <Req_Rec_Card
                request={request}
                rIndex={rIndex}
                setFinRequestsData={setFinRequestsData}
                setRawData={setRawData}
                selectedRequests={selectedRequests}
                handleCheckboxChange={handleCheckboxChange}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Helper functions for time grouping
const getDateRanges = (viewMode, startDate = new Date()) => {
  const ranges = [];

  if (viewMode === "daily") {
    // For daily view, we'll show last 30 days
    for (let i = 0; i < 30; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() - i);

      const start = new Date(day);
      start.setHours(0, 0, 0, 0);

      const end = new Date(day);
      end.setHours(23, 59, 59, 999);

      ranges.push([start, end]);
    }
  } else if (viewMode === "weekly") {
    // For weekly view, set to start of the week (Monday)
    const currentDay = startDate.getDay();
    const diff = currentDay === 0 ? 6 : currentDay - 1; // If Sunday (0), set to 6 days back, else current day - 1

    const mondayOfCurrentWeek = new Date(startDate);
    mondayOfCurrentWeek.setDate(startDate.getDate() - diff);
    mondayOfCurrentWeek.setHours(0, 0, 0, 0);

    // Show 4 weeks (starting each Monday)
    for (let i = 0; i < 4; i++) {
      const weekStart = new Date(mondayOfCurrentWeek);
      weekStart.setDate(mondayOfCurrentWeek.getDate() - i * 7);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      ranges.push([weekStart, weekEnd]);
    }
  } else if (viewMode === "monthly") {
    // For monthly view, show last 12 months
    for (let i = 0; i < 12; i++) {
      const month = new Date(
        startDate.getFullYear(),
        startDate.getMonth() - i,
        1
      );

      const monthStart = new Date(month);
      monthStart.setHours(0, 0, 0, 0);

      const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
      monthEnd.setHours(23, 59, 59, 999);

      ranges.push([monthStart, monthEnd]);
    }
  }

  return ranges;
};

// Check if a date falls within a range
const isDateInRange = (date, range) => {
  if (!date || !range || range.length !== 2) return false;
  const timestamp = new Date(date).getTime();
  return timestamp >= range[0].getTime() && timestamp <= range[1].getTime();
};

// Group requests by project
const groupRequestsByProject = (requests) => {
  const groups = {};

  requests.forEach((request) => {
    try {
      const projectDetails = JSON.parse(request.ProjectDetails || "{}");
      const projectName = Object.values(projectDetails)[0] || "Unknown Project";

      if (!groups[projectName]) {
        groups[projectName] = [];
      }

      groups[projectName].push(request);
    } catch (error) {
      console.error("Error parsing project details:", error);
    }
  });

  return groups;
};

// Calculate summary statistics for a group of requests
const calculateSummary = (requests) => {
  // Use enhanced logic for total order amount (preventing duplicates)
  const totalOrderAmount = calculateOrderAmountWithBusinessLogic(requests);
  
  return requests.reduce(
    (summary, request) => {
      const amount = Number(request.AmountRequested) || 0;
      summary.totalRequested += amount;
      summary.requestCount += 1;

      // Parse the History to get the most recent status
      try {
        const history = JSON.parse(request.History || "{}");
        const latestEntry =
          Object.values(history)[Object.values(history).length - 1] || {};
        const latestStatus = latestEntry.status;

        if (latestStatus === "Approved") {
          summary.approved += amount;
          summary.approvedCount += 1;
        } else if (latestStatus === "Declined") {
          summary.declined += amount;
          summary.declinedCount += 1;
        } else {
          summary.pending += amount;
          summary.pendingCount += 1;
        }
      } catch (error) {
        console.error("Error parsing history:", error);
      }

      return summary;
    },
    {
      total: totalOrderAmount, // Use the enhanced duplicate-free calculation
      totalRequested: 0, // Keep track of requested amounts separately
      approved: 0,
      pending: 0,
      declined: 0,
      requestCount: 0,
      approvedCount: 0,
      pendingCount: 0,
      declinedCount: 0,
    }
  );
};

// Time-Based View Component
const TimeBasedView = ({
  viewMode,
  finRequestsData,
  setFinRequestsData,
  setRawData,
  selectedRequests,
  handleCheckboxChange,
}) => {
  const [openTimeGroups, setOpenTimeGroups] = useState({});
  const [openProjectGroups, setOpenProjectGroups] = useState({});

  // Generate date ranges based on view mode
  const dateRanges = useMemo(() => getDateRanges(viewMode), [viewMode]);

  // Initialize open state for first time group
  useEffect(() => {
    if (dateRanges.length > 0) {
      const newOpenGroups = {};
      dateRanges.forEach((range, index) => {
        const key = range[0].toISOString();
        newOpenGroups[key] = index === 0; // Only first group open by default
      });
      setOpenTimeGroups(newOpenGroups);

      // Reset project groups when view mode changes
      setOpenProjectGroups({});
    }
  }, [dateRanges, viewMode]);

  // Group requests by time ranges
  const groupedRequests = useMemo(() => {
    const groups = {};

    dateRanges.forEach((range) => {
      const key = range[0].toISOString();
      const requestsInRange = finRequestsData.filter((request) =>
        isDateInRange(request.DOFReq, range)
      );

      // Extract project names for this time range
      const projectNames = [];
      requestsInRange.forEach((request) => {
        try {
          const projectDetails = JSON.parse(request.ProjectDetails || "{}");
          const projectName = Object.values(projectDetails)[0];
          if (projectName && !projectNames.includes(projectName)) {
            projectNames.push(projectName);
          }
        } catch (error) {
          console.error("Error parsing project details:", error);
        }
      });

      groups[key] = {
        range,
        requests: requestsInRange,
        projectNames,
      };
    });

    return groups;
  }, [finRequestsData, dateRanges]);

  // Calculate statistics for each project in a time group
  const calculateProjectSummary = (requests) => {
    const projects = {};

    requests.forEach((request) => {
      try {
        const projectDetails = JSON.parse(request.ProjectDetails || "{}");
        const projectName =
          Object.values(projectDetails)[0] || "Unknown Project";

        if (!projects[projectName]) {
          projects[projectName] = {
            total: 0,
            approved: 0,
            pending: 0,
            approvedCount: 0,
            pendingCount: 0,
            requests: [],
          };
        }

        const amount = Number(request.AmountRequested) || 0;
        projects[projectName].requests.push(request);
        projects[projectName].total += amount;

        // Parse history to determine status
        try {
          const history = JSON.parse(request.History || "{}");
          const latestEntry =
            Object.values(history)[Object.values(history).length - 1] || {};
          const latestStatus = latestEntry.status;

          if (latestStatus === "Approved") {
            projects[projectName].approved += amount;
            projects[projectName].approvedCount += 1;
          } else {
            projects[projectName].pending += amount;
            projects[projectName].pendingCount += 1;
          }
        } catch (error) {
          console.error("Error parsing history:", error);
        }
      } catch (error) {
        console.error("Error parsing project details:", error);
      }
    });

    return projects;
  };

  // Toggle time group's open/closed state
  const toggleTimeGroup = (key) => {
    setOpenTimeGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Toggle project group's open/closed state
  const toggleProjectGroup = (timeKey, projectKey) => {
    const groupKey = `${timeKey}-${projectKey}`;
    setOpenProjectGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  return (
    <div className="time-based-view">
      {dateRanges.map((range, index) => {
        const key = range[0].toISOString();
        const { requests, projectNames } = groupedRequests[key] || {
          requests: [],
          projectNames: [],
        };

        if (requests.length === 0) return null;

        const summary = calculateSummary(requests);
        const projectGroups = calculateProjectSummary(requests);

        return (
          <div key={key} className="time-group">
            <TimeGroupHeader
              dateRange={range}
              isOpen={openTimeGroups[key]}
              toggleOpen={() => toggleTimeGroup(key)}
              summary={summary}
              projects={projectNames}
              projectGroups={projectGroups}
            />

            {openTimeGroups[key] && (
              <div className="time-group-content animate-slide-down">
                {Object.entries(projectGroups).map(
                  ([projectName, projectSummary]) => {
                    const groupKey = `${key}-${projectName}`;
                    console.log(
                      "projectSummary : ",
                      JSON.stringify(projectSummary)
                    );
                    return (
                      <ProjectGroup
                        key={groupKey}
                        projectName={projectName}
                        projectRequests={projectSummary.requests}
                        isOpen={openProjectGroups[groupKey]}
                        toggleOpen={() => toggleProjectGroup(key, projectName)}
                        setFinRequestsData={setFinRequestsData}
                        setRawData={setRawData}
                        selectedRequests={selectedRequests}
                        handleCheckboxChange={handleCheckboxChange}
                        summary={projectSummary}
                      />
                    );
                  }
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const filterData = (
  resultsArr,
  statusFilter,
  projectFilter,
  DateRangeValue,
  categoryFilter
) => {
  let tempSet = resultsArr.filter(
    (item) =>
      (item.Status === statusFilter || statusFilter === "All") &&
      (DateRangeValue.every((date) => date === null) ||
        isDateLiesCheck(item.DOFReq, DateRangeValue)) &&
      (item.ProjectDetails.includes(projectFilter) ||
        projectFilter === "All") && // here projectDetails is stingified JSON {ProjectId:projectName} // parse and check for more accurate filters
      (item.Category === categoryFilter || categoryFilter === "All")
  );

  return tempSet;
};

const Requests_Fin = () => {
  const location = useLocation();
  const data = location?.state?.data;
  const userContext = useContext(UserContext);
  const [isLoading, setLoading] = useState(true);
  const [finRequestsData, setFinRequestsData] = useState([]); //data to display
  const [statusFilter, setStatusFilter] = useState("All");
  const [allData, setAllData] = useState([]); //data to download
  const [rawData, setRawData] = useState([]);
  const [DateRangeValue, setDateRangeValue] = useState([null, null]);
  const [highlightdivuuid, setHighlightdivuuid] = useState(0);
  const [selectedRequests, setSelectedRequests] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const [expandingInProgress, setExpandingInProgress] = useState(false);
  const [viewMode, setViewMode] = useState("daily"); // 'daily', 'weekly', or 'monthly'
  const [openTimeGroups, setOpenTimeGroups] = useState({}); // For TimeBasedView
  const dateRanges = useMemo(() => getDateRanges(viewMode), [viewMode]); // For TimeBasedView

  const [generateSelectedReqPDF, setGenerateSelectedReqPDF] = useState({
    modal: false,
    downloadable: false,
  });

  const [projectFilterName, setProjectFilterName] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");

  // State to track which date groups are open
  const [openDateGroups, setOpenDateGroups] = useState({});
  const [openProjectGroups, setOpenProjectGroups] = useState({}); // For TimeBasedView

  const getFinanceRequest = async () => {
    const opportunityService = new OpportunityService();
    const reqData = await opportunityService.getFinanceRequest(
      userContext.user.Firm
    );
    
    setRawData(reqData.results);
    setLoading(false);
    const allData = await opportunityService.getAllFinanceRequest(
      userContext.user.Firm
    );
    setAllData(allData.results);
    console.log("amount added here", reqData.results);
  };

  useEffect(() => {
    if (location.state?.data) {
      console.log("changed", location, location.state);
      let data = location.state.data;
      setHighlightdivuuid(data?.requestId);
    }
  }, [location.state]);

  useEffect(() => {
    if (highlightdivuuid) {
      const cartid = document.getElementById(`${highlightdivuuid}`);
      cartid?.focus();
      cartid?.classList.add("highlightdiv");
      // console.log(cartid, "should focus this");
    }
  }, [finRequestsData, highlightdivuuid]);

  useEffect(() => {
    //fetch the request data
    getFinanceRequest();
  }, []);

  useEffect(() => {
    if (rawData) {
      const filteredData = filterData(
        rawData,
        statusFilter,
        projectFilterName,
        DateRangeValue,
        categoryFilter
      );
      // console.log("filteredData Data", filteredData);
      
      // Initialize global order registry with the filtered data
      globalOrderRegistry.initialize(filteredData);
      
      setFinRequestsData(filteredData);
      data && setHighlightdivuuid(data.requestId);
    } else {
      // Reset registry when no data
      globalOrderRegistry.reset();
      setFinRequestsData([]);
    }
  }, [
    rawData,
    statusFilter,
    DateRangeValue,
    projectFilterName,
    categoryFilter,
  ]);

  // Group requests by date
  const getGroupedRequests = useMemo(() => {
    const dateGroups = {};

    finRequestsData.forEach((request) => {
      // Extract request date from DOFReq or History
      let requestDate;

      if (request.DOFReq) {
        requestDate = new Date(request.DOFReq).toDateString();
      } else if (request.History) {
        try {
          const history = JSON.parse(request.History);
          const firstTimestamp = Object.keys(history)[0];
          requestDate = new Date(parseInt(firstTimestamp)).toDateString();
        } catch (error) {
          console.error("Error parsing history:", error);
          requestDate = new Date().toDateString();
        }
      } else {
        requestDate = new Date().toDateString();
      }

      if (!dateGroups[requestDate]) {
        dateGroups[requestDate] = [];
      }
      dateGroups[requestDate].push(request);
    });

    // Sort dates in descending order (newest first)
    return Object.entries(dateGroups).sort(
      ([dateA], [dateB]) => new Date(dateB) - new Date(dateA)
    );
  }, [finRequestsData]);

  // Group requests by date and initialize open state
  useEffect(() => {
    if (finRequestsData.length > 0 && getGroupedRequests.length > 0) {
      // Set only the first group to be open by default, others closed
      const newOpenState = {};

      getGroupedRequests.forEach(([date], index) => {
        newOpenState[date] = index === 0; // Only open the first group by default
      });

      setOpenDateGroups(newOpenState);
    }
  }, [finRequestsData, getGroupedRequests]);

  const calculateTotalRequestedAmount = () => {
    let totalAmount = 0;

    if (rawData || finRequestsData) {
      for (let i = 0; i < finRequestsData.length; i++) {
        const item = finRequestsData[i];
        console.log("added value of totalAmount is", totalAmount);
        if (
          item.AmountRequested !== "undefined" &&
          item.AmountRequested !== null
        ) {
          console.log("requested amount is", item.AmountRequested);
          totalAmount += Number(item.AmountRequested);
          console.log("total amount is", totalAmount);
        }
      }
    }

    return totalAmount;
  };

  const calculateTotalOrderAmount = () => {
    return calculateOrderAmountWithBusinessLogic(finRequestsData);
  };

  const receivedAmount = useMemo(
    () => calculateTotalRequestedAmount(),
    [rawData, finRequestsData]
  );

  const totalOrderAmount = useMemo(
    () => calculateTotalOrderAmount(),
    [finRequestsData]
  );

  const getProjectNamesFromReq = () => {
    const projectNamesObj = rawData?.reduce((accumulator, item) => {
      const projectDetails = JSON.parse(item.ProjectDetails);

      if (
        typeof projectDetails === "object" &&
        Object.keys(projectDetails).length > 0
      ) {
        const projectName = Object.values(projectDetails)[0];
        accumulator[projectName] = true;
      }

      return accumulator;
    }, {});

    return ["All", ...Object.keys(projectNamesObj || {})];
  };

  const projectNamesToFilter = useMemo(
    () => getProjectNamesFromReq(),
    [rawData]
  );

  const getCategoryNamesFromReq = () => {
    const categorySet = new Set();
    return (
      rawData?.reduce(
        (accumulator, item) => {
          const category = item.Category;

          if (!categorySet.has(category) && category) {
            categorySet.add(category);
            accumulator.push(category);
          }

          return accumulator;
        },
        ["All"]
      ) || ["All"]
    );
  };

  const categoryNamesToFilter = useMemo(
    () => getCategoryNamesFromReq(),
    [rawData]
  );

  const sumOfSelectedRequests = useMemo(
    () =>
      Object.values(selectedRequests).reduce(
        (sum, request) => sum + Number(request.AmountRequested),
        0
      ),
    [selectedRequests]
  );

  const handleCheckboxChange = (request) => {
    setSelectedRequests((prevSelected) => {
      const isSelected = prevSelected[request.uuid];

      // Create a copy of the selected requests object
      const newSelected = { ...prevSelected };

      if (isSelected) {
        // If the request is already in the object, remove it
        delete newSelected[request.uuid];
      } else {
        // If the request is not in the object, add it
        newSelected[request.uuid] = request;
      }

      return newSelected;
    });
  };

  // Toggle all date groups with visual feedback
  const toggleAllDateGroups = () => {
    const newState = !expandAll;
    setExpandAll(newState);
    setExpandingInProgress(true);

    // Create a new object with all groups set to the expand state
    const newOpenState = {};
    getGroupedRequests.forEach(([date]) => {
      newOpenState[date] = newState;
    });

    // For time groups too
    const newTimeOpenState = {};
    if (dateRanges && dateRanges.length > 0) {
      dateRanges.forEach((range) => {
        const key = range[0].toISOString();
        newTimeOpenState[key] = newState;
      });
      setOpenTimeGroups(newTimeOpenState);
    }

    // Use setTimeout to allow the UI to update with the loading indicator
    setTimeout(() => {
      setOpenDateGroups(newOpenState);
      // Add a small delay to ensure the UI updates before removing the indicator
      setTimeout(() => {
        setExpandingInProgress(false);
      }, 500);
    }, 50);
  };

  // Toggle a date group's open/closed state
  const toggleDateGroup = (date) => {
    setOpenDateGroups((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  return (
    <>
      <div
        className="requests-filtersContainer"
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "100%",
          margin: "0 auto 20px",
          alignItems: "center",
          padding: "16px 20px",
          backgroundColor: "#ffffff",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          borderBottom: "2px solid #fdd34d",
          boxSizing: "border-box",
          flexWrap: "wrap",
          gap: "15px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              flex: 2,
            }}
          >
            <DropDownMUI
              placeholder={"Project"}
              disabeSorting={true}
              data={projectNamesToFilter}
              value={projectFilterName}
              onChange={(value) => {
                setProjectFilterName(value);
              }}
              size={"small"}
              custStyles={{ minWidth: "140px" }}
            />
            <DropDownMUI
              placeholder={"Category"}
              disabeSorting={true}
              data={categoryNamesToFilter}
              value={categoryFilter}
              onChange={(value) => {
                setCategoryFilter(value);
              }}
              size={"small"}
              custStyles={{ minWidth: "130px" }}
            />
            <DropDownMUI
              placeholder={"Status"}
              disabeSorting={true}
              data={["All", "Requested", "Approved", "Declined", "On hold"]}
              value={statusFilter === "Request" ? "Requested" : statusFilter}
              onChange={(value) => {
                setStatusFilter(() => {
                  switch (value) {
                    case "Requested":
                      return "Request";
                    default:
                      return value;
                  }
                });
              }}
              size={"small"}
              custStyles={{ minWidth: "120px" }}
            />
            <div>
              <CustomDateRangePicker
                DateRange={DateRangeValue}
                setDateRange={setDateRangeValue}
              />
            </div>

            <TimeBubbles viewMode={viewMode} setViewMode={setViewMode} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <button
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: expandingInProgress ? "#fffdf5" : "#f6f6f6",
                border: `1px solid ${
                  expandingInProgress ? "#fdd34d" : "#e0e0e0"
                }`,
                padding: "6px 14px",
                borderRadius: "6px",
                cursor: expandingInProgress ? "default" : "pointer",
                fontSize: "13px",
                fontWeight: "500",
                color: "#555",
                transition: "all 0.2s ease",
                position: "relative",
                minWidth: "110px",
                justifyContent: "center",
                whiteSpace: "nowrap",
              }}
              onClick={expandingInProgress ? null : toggleAllDateGroups}
              disabled={expandingInProgress}
            >
              {expandingInProgress ? (
                <>
                  <div
                    style={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "50%",
                      border: "2px solid #fdd34d",
                      borderTopColor: "transparent",
                      animation: "spin 1s linear infinite",
                      marginRight: "8px",
                      display: "inline-block",
                    }}
                  />
                  <style>
                    {`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}
                  </style>
                  Processing...
                </>
              ) : (
                <>
                  <span
                    style={{
                      fontSize: "14px",
                      transform: expandAll ? "rotate(90deg)" : "rotate(0)",
                      display: "inline-block",
                      width: "14px",
                      height: "14px",
                      textAlign: "center",
                      lineHeight: "14px",
                      transition: "transform 0.3s ease",
                    }}
                  >
                    ▶
                  </span>
                  {expandAll ? "Collapse All" : "Expand All"}
                </>
              )}
            </button>

            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#555",
                  whiteSpace: "nowrap",
                  fontWeight: "500",
                }}
              >
                Total Amount:
              </p>
              <p
                style={{
                  fontWeight: 700,
                  fontSize: "18px",
                  margin: 0,
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                }}
              >
                <span style={{ fontSize: "16px", marginRight: "2px" }}>₹</span>
                {receivedAmount.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
              </p>
            </div>

            {Object.keys(selectedRequests).length > 0 && (
              <button
                className="req-card-btn"
                style={{
                  color: "#333",
                  backgroundColor: "#fdd34d",
                  border: "none",
                  padding: "8px 15px",
                  borderRadius: "6px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 4px rgba(253, 211, 77, 0.25)",
                }}
                onClick={async () => {
                  setGenerateSelectedReqPDF({
                    modal: true,
                    downloadable: true,
                  });
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Generate PDF ({Object.keys(selectedRequests).length})
              </button>
            )}
          </div>
        </div>
      </div>

      <style>
        {`
          .time-range-bubbles {
            display: flex;
            align-items: center;
            background-color: #eaeaea;
            border-radius: 50px;
            padding: 3px;
            height: 32px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            position: relative;
            width: 280px;
          }
          
          .time-bubble {
            padding: 8px 0;
            border-radius: 50px;
            cursor: pointer;
            font-size: 13px;
            font-weight: 500;
            color: #555;
            transition: all 0.2s ease;
            white-space: nowrap;
            flex: 1;
            text-align: center;
            z-index: 1;
            position: relative;
          }
          
          .time-bubble.active {
            color: #333;
            font-weight: 600;
          }
          
          .time-bubble:hover:not(.active) {
            color: #333;
          }
          
          .time-range-bubbles:before {
            content: '';
            position: absolute;
            width: 33.33%;
            height: calc(100% - 6px);
            top: 3px;
            left: 3px;
            background-color: white;
            border-radius: 50px;
            z-index: 0;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            transition: all 0.3s ease;
          }
          
          .time-range-bubbles[data-active="weekly"]:before {
            transform: translateX(100%);
          }
          
          .time-range-bubbles[data-active="monthly"]:before {
            transform: translateX(200%);
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      <div
        style={{
          height: "78vh",
          overflow: "auto",
          width: "100%",
          maxWidth: "100%",
          margin: "0",
          padding: "0",
          boxSizing: "border-box",
        }}
      >
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px 0",
              width: "100%",
            }}
          >
            <CircularProgress
              style={{ width: "36px", height: "36px", color: "#fdd34d" }}
            />
          </div>
        ) : finRequestsData.length > 0 ? (
          viewMode === "daily" && !DateRangeValue[0] && !DateRangeValue[1] ? (
            // Original date-group view for daily mode without custom date range
            (console.log("getGroupedRequests : ", getGroupedRequests[0]),
            getGroupedRequests.map(([date, requests]) => (
              <DateGroup
                key={date}
                date={date}
                requests={requests}
                isOpen={openDateGroups[date]}
                toggleOpen={() => toggleDateGroup(date)}
                setFinRequestsData={setFinRequestsData}
                setRawData={setRawData}
                selectedRequests={selectedRequests}
                handleCheckboxChange={handleCheckboxChange}
              />
            )))
          ) : (
            // New time-based view for weekly/monthly or when custom date range is selected
            <TimeBasedView
              viewMode={viewMode}
              finRequestsData={finRequestsData}
              setFinRequestsData={setFinRequestsData}
              setRawData={setRawData}
              selectedRequests={selectedRequests}
              handleCheckboxChange={handleCheckboxChange}
            />
          )
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
              color: "#666",
              backgroundColor: "#fffdf5",
              borderRadius: "8px",
              margin: "20px 0",
              border: "1px solid #fdd34d",
              width: "100%",
            }}
          >
            <h4 style={{ margin: 0 }}>
              No finance requests match your filter criteria
            </h4>
          </div>
        )}
        {generateSelectedReqPDF.modal && (
          <Modal
            open={generateSelectedReqPDF.modal}
            onClose={() =>
              setGenerateSelectedReqPDF({ modal: false, downloadable: false })
            }
          >
            <Box
              sx={{
                position: "relative",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                overflow: "auto",
                bgcolor: "background.paper",
                borderRadius: "10px",
                boxShadow: 24,
                p: 4,
                minWidth: "40%",
                maxHeight: "90%",
                maxWidth: "4%",
              }}
            >
              <div>
                {generateSelectedReqPDF.downloadable && (
                  <PDFDownloadLink
                    document={
                      <SelectedRequestsPDF
                        selectedRequests={selectedRequests}
                        sumOfSelectedRequests={sumOfSelectedRequests}
                      />
                    }
                    fileName={`payments-${new Date().toLocaleDateString(
                      "en-GB"
                    )}`}
                  >
                    {({ url, loading }) => {
                      return loading
                        ? "Loading document..."
                        : url
                        ? "Download now!"
                        : "Loading...";
                    }}
                  </PDFDownloadLink>
                )}
              </div>
            </Box>
          </Modal>
        )}
      </div>
    </>
  );
};
export { Requests_Fin, filterData };


